import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { api } from "@/lib/api";
import { getProductIdForService } from "@/lib/getProductIdForService";
import { getOpenAIConfig } from "@/app/actions/env";
import { useProductSetup } from "@/hooks/useProductSetup";

export interface UseSetupProps {
    deployId: string;
    orderId?: string;
    productId?: string;
    steps?: Array<{ step_title?: string; title?: string; config?: unknown }>;
    isOnboarding?: boolean;
    /** Parent has finished loading order; deployId is final or we know there is none. */
    deployIdResolved?: boolean;
    /** Parent loaded order and it has no taskId. Only then we start deploy/init when we have no deploy_id. */
    orderHasNoTaskId?: boolean;
}

const SETUP_TYPING_MESSAGES = [
    "กำลังดำเนินการติดตั้งระบบ",
    "กำลังติดตั้งโมดูลเสริม",
    "กำลังปรับจูนประสิทธิภาพ",
    "กำลังตั้งค่าระบบฐานข้อมูล",
    "กำลังตรวจสอบสิทธิ์การเข้าใช้งาน",
    "กำลังเชื่อมต่อเซิร์ฟเวอร์หลัก",
];

const POLL_INTERVAL_MS = 5000;
const MAX_RETRIES = 3;
const ORDER_POLL_INTERVAL_MS = 15000;

export function getSetupStatusText(
    initSuccess: boolean,
    isInitChecking: boolean,
    isSubmittingCredentials: boolean,
    typingText: string,
    setupError: string | null,
    isRetrying: boolean,
    initStatusText: string | null
): string {
    if (initSuccess) return `✅ ติดตั้งสำเร็จแล้ว`;
    if (isRetrying) return `⚠️ พบข้อผิดพลาด กำลังเตรียมติดตั้งใหม่อีกครั้ง...`;
    if (setupError) return `❌ เกิดข้อผิดพลาด`;
    // ช่วง deploy/init ใช้ effect พิมพ์ทีละตัวตามเดิม (typingText = displayText)
    if (isSubmittingCredentials) return typingText;
    // ถ้ามีข้อความกำลังพิมพ์ (ทั้งจาก deploy/init หรือ init/status) ให้แสดงก่อน
    if (typingText) return typingText;
    // ถ้าไม่มี typing แล้ว แต่ยังมีประโยคล่าสุดจาก init/status ให้ใช้ต่อ
    if (initStatusText) return initStatusText;
    // fallback เริ่มต้น (ก่อนมีค่า initStatusText ครั้งแรก)
    return SETUP_TYPING_MESSAGES[0];
}

export function useSetup({
    deployId,
    orderId,
    productId,
    steps: stepsProp = [],
    isOnboarding = false,
    deployIdResolved = true,
    orderHasNoTaskId = false,
}: UseSetupProps) {
    const { setup: productSetup, loading: productSetupLoading, error: productSetupError } = useProductSetup(productId);
    const setupStepsFromProduct =
        (productSetup as { setups?: UseSetupProps["steps"]; steps?: UseSetupProps["steps"] } | null | undefined)?.setups ||
        (productSetup as { setups?: UseSetupProps["steps"]; steps?: UseSetupProps["steps"] } | null | undefined)?.steps ||
        [];
    const steps = stepsProp.length ? stepsProp : setupStepsFromProduct;

    const [orderData, setOrderData] = useState<{ buyerId?: string } & Record<string, unknown> | null>(null);
    const [isOrderLoading, setIsOrderLoading] = useState(false);
    const [initSuccess, setInitSuccess] = useState(false);
    const [localDeployId, setLocalDeployId] = useState(deployId);
    const [deployIdFromCredentialsSetup, setDeployIdFromCredentialsSetup] = useState<string | null>(null);
    const [localStep, setLocalStep] = useState(1);
    const [isCheckingInitial, setIsCheckingInitial] = useState(true);
    const [sequenceStarted, setSequenceStarted] = useState(false);
    const [credentialsSubmitted, setCredentialsSubmitted] = useState(false);
    const [businessSubmitted, setBusinessSubmitted] = useState(false);
    const [isSubmittingCredentials, setIsSubmittingCredentials] = useState(false);
    const [isInitChecking, setIsInitChecking] = useState(false);
    const [initRequestDone, setInitRequestDone] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [setupError, setSetupError] = useState<string | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const credentialsAttemptedRef = useRef(false);
    const checkInProgressRef = useRef(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const localStepRef = useRef(localStep);
    const wsRef = useRef<WebSocket | null>(null);
    // Guard: prevents both WS path and HTTP path from calling /v1/init simultaneously
    const initCalledRef = useRef(false);

    useEffect(() => {
        localStepRef.current = localStep;
    }, [localStep]);

    // Poll order data (buyerId, taskId, etc.) for downstream consumers (AgentChatPanel).
    useEffect(() => {
        if (!orderId) {
            setOrderData(null);
            return;
        }

        let isFirstLoad = true;
        let cancelled = false;

        const fetchOrder = async () => {
            if (isFirstLoad) setIsOrderLoading(true);
            try {
                const data = await api.orders.getById(orderId);
                const normalized =
                    data && typeof data === "object" && "order" in data
                        ? ((data as { order?: unknown }).order ?? data)
                        : data;
                if (!cancelled) setOrderData(normalized as { buyerId?: string } & Record<string, unknown>);
            } catch (error) {
                console.error("[useSetup] Failed to fetch order:", error);
            } finally {
                if (isFirstLoad) {
                    if (!cancelled) setIsOrderLoading(false);
                    isFirstLoad = false;
                }
            }
        };

        fetchOrder();
        const interval = setInterval(fetchOrder, ORDER_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [orderId]);

    // "In setup" = we're in onboarding or we've been through setup steps (so we show setup UI until success + 2s message).
    const [inSetup, setInSetup] = useState(!!isOnboarding);

    useEffect(() => {
        if (deployId && deployId !== "temp-onboarding") setLocalDeployId(deployId);
    }, [deployId]);
    useEffect(() => {
        if (isOnboarding || localStep >= 1) setInSetup(true);
    }, [isOnboarding, localStep]);

    const triggerRetry = (msg: string) => {
        if (retryCount >= MAX_RETRIES) {
            setSetupError(`${msg} (หยุดหลังจากลอง ${MAX_RETRIES} ครั้ง)`);
            setIsRetrying(false);
            return;
        }
        setSetupError(msg);
        setIsRetrying(true);
        setRetryCount((c) => c + 1);
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        const delay = msg.includes("429") ? 8000 : 3000;
        retryTimeoutRef.current = setTimeout(() => {
            const step = localStepRef.current;
            setSetupError(null);
            setIsRetrying(false);
            setInitRequestDone(false);
            initCalledRef.current = false;
            if (step === 1) {
                setCredentialsSubmitted(false);
                credentialsAttemptedRef.current = false;
            } else {
                setBusinessSubmitted(false);
                if (step === 3) setLocalStep(2);
            }
        }, delay);
    };

    // Typing text for "กำลัง..." message
    const [typingIndex, setTypingIndex] = useState(0);
    const [displayText, setDisplayText] = useState(SETUP_TYPING_MESSAGES[0]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [initStatusText, setInitStatusText] = useState<string | null>(null);
    const [initStatusTypingText, setInitStatusTypingText] = useState<string>("");
    const lastInitStatusTextRef = useRef<string | null>(null);
    // Busy = ช่วง deploy/init เท่านั้น (ไม่รวม init/status) เพื่อไม่ให้ effect พิมพ์ทีละตัวไปรบกวนข้อความของ init/status
    const isBusy = isSubmittingCredentials;

    // typing effect สำหรับ deploy/init (submit credentials/business)
    useEffect(() => {
        if (!isBusy || initSuccess || setupError || isRetrying) {
            setDisplayText("");
            return;
        }
        const word = SETUP_TYPING_MESSAGES[typingIndex];
        const t = setTimeout(() => {
            if (!isDeleting && displayText === word) {
                setTimeout(() => setIsDeleting(true), 1500);
            } else if (isDeleting && displayText === "") {
                setIsDeleting(false);
                setTypingIndex((p) => (p + 1) % SETUP_TYPING_MESSAGES.length);
            } else {
                const len = displayText.length + (isDeleting ? -1 : 1);
                setDisplayText(word.slice(0, len));
            }
        }, isDeleting ? 30 : 50);
        return () => clearTimeout(t);
    }, [displayText, isDeleting, typingIndex, isBusy, initSuccess, setupError, isRetrying]);

    // typing effect สำหรับข้อความที่ได้จาก /admin/init/status (initStatusText)
    useEffect(() => {
        if (!initStatusText) {
            setInitStatusTypingText("");
            return;
        }
        let index = 0;
        const full = initStatusText;
        setInitStatusTypingText("");
        let timer: ReturnType<typeof setTimeout> | null = null;
        const step = () => {
            index += 1;
            if (index > full.length) return;
            setInitStatusTypingText(full.slice(0, index));
            timer = setTimeout(step, 30);
        };
        timer = setTimeout(step, 30);
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [initStatusText]);

    // Show success checkmark for 2s then hide setup UI
    useEffect(() => {
        if (initSuccess && inSetup && !showSuccessMessage) setShowSuccessMessage(true);
    }, [initSuccess, inSetup, showSuccessMessage]);
    // When init succeeds via polling, advance step so the rest of the app can proceed (e.g. greeting).
    // On initial load we already set step=4, but when init flips later we must also do it here.
    useEffect(() => {
        if (!initSuccess) return;
        setLocalStep((prev) => (Number(prev) >= 4 ? prev : 4));
    }, [initSuccess]);
    useEffect(() => {
        if (!showSuccessMessage) return;
        const t = setTimeout(() => {
            setShowSuccessMessage(false);
            setInSetup(false);
        }, 2000);
        return () => clearTimeout(t);
    }, [showSuccessMessage]);

    const connectInitWs = useCallback((deployId: string) => {
        if (typeof window === "undefined") return;
        const wsUrl = process.env.NEXT_PUBLIC_WS_URL;
        const token = localStorage.getItem("auth_token");
        if (!wsUrl || !token) return;
        if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;

        const clean = token.trim().startsWith("Bearer ") ? token.trim().slice(7) : token.trim();
        const socket = new WebSocket(`${wsUrl}?token=${clean}`);
        wsRef.current = socket;

        socket.onopen = () => {
            socket.send(JSON.stringify({ event: "join", data: { roomId: `deploy:${deployId}` } }));
        };

        socket.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data as string) as {
                    event?: string;
                    data?: { status?: boolean; initialized?: boolean };
                };
                const isInitDone =
                    msg.event === "init_complete" ||
                    msg.event === "initialized" ||
                    msg.event === "setup_complete" ||
                    msg.data?.status === true ||
                    msg.data?.initialized === true;
                if (isInitDone) {
                    setInitSuccess(true);
                    socket.close();
                }
            } catch { /* ignore parse errors */ }
        };

        socket.onclose = () => {
            wsRef.current = null;
        };
    }, []);

    const checkInitStatus = useCallback(async (id: string) => {
        if (!id || id === "temp-onboarding" || checkInProgressRef.current) return { success: false, initialized: false };
        checkInProgressRef.current = true;
        // ทุกครั้งที่รีเรียก /admin/init/status สุ่มประโยคที่ต่างจากข้อความล่าสุด เพื่อให้เห็นว่ามีการเปลี่ยน
        const prev = lastInitStatusTextRef.current;
        const others = SETUP_TYPING_MESSAGES.filter((m) => m !== prev);
        const next =
            others.length > 0
                ? others[Math.floor(Math.random() * others.length)]
                : SETUP_TYPING_MESSAGES[Math.floor(Math.random() * SETUP_TYPING_MESSAGES.length)];
        lastInitStatusTextRef.current = next;
        setInitStatusText(next);
        setIsInitChecking(true);
        try {
            const url = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${id}/admin/init/status`;
            const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
            const headers: Record<string, string> = { "Content-Type": "application/json" };
            if (token) {
                const clean = token.trim().startsWith("Bearer ") ? token.trim().slice(7) : token.trim();
                headers["Authorization"] = `Bearer ${clean}`;
            }
            const res = await axios.post("/api/proxy", { url, method: "GET", headers }, { timeout: 30000 });
            const ok = res.data?.status === true;
            if (ok) setInitSuccess(true);
            return { success: true, initialized: ok };
        } catch (err) {
            console.error("[useSetup] check init status failed:", err);
            return { success: false, initialized: false };
        } finally {
            setIsInitChecking(false);
            checkInProgressRef.current = false;
        }
    }, []);

    // Phase 0: On load / when deployId changes — check status or start sequence
    useEffect(() => {
        if (credentialsSubmitted) {
            setIsCheckingInitial(false);
            return;
        }
        const id = deployId && deployId !== "temp-onboarding" ? deployId : (localDeployId !== "temp-onboarding" ? localDeployId : null);
        if (id) {
            (async () => {
                const r = await checkInitStatus(id);
                setIsCheckingInitial(false);
                if (r.success && r.initialized) {
                    setLocalStep(4);
                    setInSetup(false);
                    return;
                }
                if (r.success && !r.initialized) {
                    // We have a deployment id, but init is NOT completed.
                    // On refresh after a failed init, polling alone won't recover because init isn't re-triggered.
                    // So: drop back to step 2 to re-run /v1/init (without redeploy), then polling will start after initRequestDone.
                    setInSetup(true);
                    setInitRequestDone(false);
                    setBusinessSubmitted(false);
                    setLocalStep(2);
                    setSequenceStarted(true);
                    return;
                }
                // We have a deploy_id but the status check failed (network/timeout). Do NOT start
                // the sequence — that would call deploy and overwrite the existing deployment.
                // User can refresh to retry the check.
            })();
            return;
        }
        if (!deployIdResolved || !orderHasNoTaskId) return;
        setIsCheckingInitial(false);
        setSequenceStarted(true);
    }, [deployId, localDeployId, credentialsSubmitted, deployIdResolved, orderHasNoTaskId, checkInitStatus]);

    // Sequence: Deploy (Credentials Setup) via /api/proxy
    // deploy_id will come back via WebSocket → runInitFromWsDeployId handles Business Setup + order update
    const submitDeploy = useCallback(async () => {
        const step = steps.find((s) => s.step_title === "Credentials Setup" || s.title === "Credentials Setup");
        if (!step?.config || !orderId) return;
        credentialsAttemptedRef.current = true;
        setIsSubmittingCredentials(true);
        try {
            let config = step.config as Record<string, unknown>;
            if (typeof config === "string") {
                try {
                    config = JSON.parse(config) as Record<string, unknown>;
                } catch {
                    return;
                }
            }
            let endpointUrl = (config.endpoint_url as string) || (config.endpoint as string) || "";
            const methodFromConfig = (config.endpoint_method as string) || "POST";

            // Clean "METHOD URL" legacy format
            if (endpointUrl.includes(" ")) {
                const parts = endpointUrl.split(" ");
                if (["GET", "POST", "PUT", "DELETE", "PATCH"].includes(parts[0])) {
                    endpointUrl = parts[1];
                }
            }
            if (!endpointUrl) throw new Error("Missing endpoint_url for Credentials Setup");

            const env = await getOpenAIConfig();
            const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
            const headers: Record<string, string> = { "Content-Type": "application/json" };
            if (token) headers["Authorization"] = `Bearer ${token.trim().startsWith("Bearer ") ? token.trim().slice(7) : token.trim()}`;

            // Resolve workspace name and service ID from order
            let orderServiceId: string | undefined;
            try {
                const order = (await api.orders.getById(orderId)) as { workspaceId?: string; serviceId?: string } | null;
                orderServiceId = order?.serviceId;
            } catch {
            }

            const deployPayload = {
                project_id: getProductIdForService(orderServiceId),
                service_id: orderServiceId,
                order_id: orderId,
                ...env,
            };

            // Fire deploy_project — response only contains request_id
            // Actual deploy_id arrives via WebSocket → runInitFromWsDeployId takes over
            await axios.post(
                "/api/proxy",
                { url: endpointUrl, method: methodFromConfig, headers, data: deployPayload },
                { timeout: 240000 }
            );
        } catch (err) {
            console.error("[useSetup] Deploy failed:", err);
            triggerRetry("Deploy failed");
        } finally {
            setIsSubmittingCredentials(false);
        }
    // triggerRetry is stable (useCallback) but would cause unnecessary re-runs if in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steps, orderId, productId]);
    useEffect(() => {
        if (isCheckingInitial || !sequenceStarted || !steps.length || !orderId || initSuccess || isSubmittingCredentials) return;
        const stepNo = Number(localStep);
        // Only run deploy when we truly have no deploy_id from parent. If parent passed a real deployId
        // (e.g. from order), we must not call deploy — it would overwrite the existing deployment.
        const hasDeployIdFromParent = deployId && deployId !== "temp-onboarding";
        if (stepNo === 1 && !credentialsSubmitted && !credentialsAttemptedRef.current && !hasDeployIdFromParent) {
            submitDeploy();
        }
        // step 2 (/v1/init) is auto-called after deploy via /api/proxy
    }, [isCheckingInitial, sequenceStarted, steps.length, orderId, localStep, credentialsSubmitted, businessSubmitted, initSuccess, isSubmittingCredentials, deployId, submitDeploy]);

    // Poll /admin/init/status every 5s after init request sent until success
    // Also connect WebSocket to receive init_complete event in real-time (polling kept as fallback)
    useEffect(() => {
        if (!inSetup || !initRequestDone || initSuccess) {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return;
        }
        const id = localDeployId !== "temp-onboarding" ? localDeployId : deployId;
        if (!id || id === "temp-onboarding") return;

        connectInitWs(id);

        if (!pollRef.current) {
            checkInitStatus(id);
            pollRef.current = setInterval(() => checkInitStatus(id), POLL_INTERVAL_MS);
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [inSetup, initRequestDone, initSuccess, localDeployId, deployId, checkInitStatus, connectInitWs]);

    // Called from AgentChatPanel WS handler when /api/deploy_project publishes deploy_id.
    // Triggers /v1/init with the WS-provided deploy_id. Guard prevents double-call with HTTP path.
    const runInitFromWsDeployId = useCallback(async (wsDeployId: string) => {
        if (initCalledRef.current) { 
            return; 
        }
        initCalledRef.current = true;
        if (!orderId) { 
            return; 
        }

        setIsSubmittingCredentials(true);
        try {
            const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
            const headers: Record<string, string> = { "Content-Type": "application/json" };
            if (token) headers["Authorization"] = `Bearer ${token.trim().startsWith("Bearer ") ? token.trim().slice(7) : token.trim()}`;

            let workspaceName = "My Business";
            try {
                const order = (await api.orders.getById(orderId)) as { workspaceId?: string } | null;
                const list = (await api.workspaces.list()) as { workspaces?: Array<{ id: string; name: string }> };
                const wsList = list?.workspaces || [];
                if (order?.workspaceId && wsList.length) {
                    const w = wsList.find((x) => x.id === order.workspaceId);
                    if (w) workspaceName = w.name;
                } else if (wsList.length) workspaceName = wsList[0].name;
            } catch { /**/ }

            const businessStep = steps.find((s) => s.step_title === "Business Setup" || s.title === "Business Setup");
            let initUrl = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${wsDeployId}/v1/init`;
            let initMethod = "POST";
            if (businessStep?.config) {
                let bsConfig = businessStep.config as Record<string, unknown>;
                if (typeof bsConfig === "string") { try { bsConfig = JSON.parse(bsConfig) as Record<string, unknown>; } catch { bsConfig = {}; } }
                let bsUrl = (bsConfig.endpoint_url as string) || (bsConfig.endpoint as string) || "";
                const bsMethod = (bsConfig.endpoint_method as string) || "POST";
                if (bsUrl.includes(" ")) { const parts = bsUrl.split(" "); if (["GET", "POST", "PUT", "DELETE", "PATCH"].includes(parts[0])) bsUrl = parts[1]; }
                if (bsUrl) { initUrl = bsUrl.replace("{deploy_id}", wsDeployId); initMethod = bsMethod; }
            }

            await axios.post("/api/proxy", { url: initUrl, method: initMethod, headers, data: { business_info: workspaceName } }, { timeout: 240000 });
            setBusinessSubmitted(true);
            await api.orders.update(orderId, { currentSetupStep: "3", taskId: wsDeployId });
            setDeployIdFromCredentialsSetup(wsDeployId);
            setLocalDeployId(wsDeployId);
            setCredentialsSubmitted(true);
            setLocalStep(3);
            setInitRequestDone(true);
        } catch (err) {
            initCalledRef.current = false; // allow retry
            console.error("[useSetup] runInitFromWsDeployId failed:", err);
        } finally {
            setIsSubmittingCredentials(false);
        }
    }, [orderId, steps]);

    useEffect(() => () => {
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    }, []);

    // Close WS when init succeeds or component unmounts
    useEffect(() => {
        if (initSuccess && wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, [initSuccess]);

    useEffect(() => () => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    const shouldShowSetup = initSuccess ? showSuccessMessage : inSetup || showSuccessMessage;
    // ถ้ามี typing จาก init/status ให้ใช้ก่อน displayText
    const typingForStatus = initStatusTypingText || displayText;
    const setupStatusText = shouldShowSetup
        ? getSetupStatusText(
            initSuccess,
            isInitChecking,
            isSubmittingCredentials,
            typingForStatus,
            setupError,
            isRetrying,
            initStatusText
        )
        : "";

    const effectiveDeployId = (localDeployId && localDeployId !== "temp-onboarding")
        ? localDeployId
        : (deployId && deployId !== "temp-onboarding" ? deployId : "");

    return {
        localSetupStep: localStep,
        shouldShowSetup,
        setupStatusText,
        initWsSuccess: initSuccess,
        setIsInitRequestDone: setInitRequestDone,
        isInitChecking,
        hadOnboarding: inSetup,
        showSetupComplete: showSuccessMessage,
        setupError,
        triggerAutoRetry: triggerRetry,
        runInitFromWsDeployId,
        effectiveDeployId,
        deployIdFromCredentialsSetup,
        orderData,
        isOrderLoading,
        steps,
        productSetupLoading,
        productSetupError,
    };
}
